import { useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { useUrlState } from '../hooks/useUrlState';
import { api, CommissionPeriodRow, PeriodRow } from '../lib/api';
import { fmtCurrency, fmtMonth, currentMonth } from '../lib/fmt';
import { SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge, ActionButton, Spinner, EmptyState } from '../components/ui';

type PeriodAction = 'lock' | 'approve' | 'mark-paid';

function periodState(p: PeriodRow): string {
  if (p.is_paid)     return 'paid';
  if (p.is_approved) return 'approved';
  if (p.is_locked)   return 'locked';
  if (p.has_snapshot) return 'open';
  return 'no-snapshot';
}

function stateBadge(state: string) {
  switch (state) {
    case 'paid':        return <Badge variant="green">Paid</Badge>;
    case 'approved':    return <Badge variant="blue">Approved</Badge>;
    case 'locked':      return <Badge variant="locked">Locked</Badge>;
    case 'open':        return <Badge variant="amber">Open</Badge>;
    default:            return <Badge variant="gray">No snapshot</Badge>;
  }
}

const ACTION_LABELS: Record<PeriodAction, { button: string; title: string; description: string; variant: 'danger' | 'primary' }> = {
  lock:        { button: 'Lock period',       title: 'Lock period',             description: 'This will permanently freeze all shipped value and bonus records for this month. Changes from future refreshes will not overwrite locked data. This cannot be undone.', variant: 'danger' },
  approve:     { button: 'Approve bonuses',   title: 'Approve bonus payouts',   description: 'This confirms the locked bonus figures are correct and ready for payout. The period must already be locked. This cannot be undone.', variant: 'primary' },
  'mark-paid': { button: 'Mark as paid',      title: 'Mark bonuses as paid',    description: 'This records that the approved bonuses have been paid to employees. The period must already be approved. This cannot be undone.', variant: 'primary' },
};

// ─── Commission periods ──────────────────────────────────────────────────────
// Same lifecycle, separate ledger. The state lives in a real status column
// rather than being derived from the payout rows, so locking is a fact about
// the period instead of a property of its contents — which is what stops the
// commission recompute from ever rewriting an approved month.

const COMMISSION_ACTION_LABELS: Record<PeriodAction, { button: string; title: string; description: string; variant: 'danger' | 'primary' }> = {
  lock:        { button: 'Lock period',         title: 'Lock commission period',      description: 'This freezes every commission payout in this month. Refreshes will no longer rebuild it, so later invoice or rate changes cannot move these figures. This cannot be undone.', variant: 'danger' },
  approve:     { button: 'Approve commissions', title: 'Approve commission payouts',  description: 'This confirms the locked commission figures are correct and ready for a payment run. The period must already be locked. This cannot be undone.', variant: 'primary' },
  'mark-paid': { button: 'Mark as paid',        title: 'Mark commissions as paid',    description: 'This records that the approved commissions have been paid to the reps. The period must already be approved. This cannot be undone.', variant: 'primary' },
};

function commissionStateBadge(status: CommissionPeriodRow['status']) {
  switch (status) {
    case 'paid':     return <Badge variant="green">Paid</Badge>;
    case 'approved': return <Badge variant="blue">Approved</Badge>;
    case 'locked':   return <Badge variant="locked">Locked</Badge>;
    default:         return <Badge variant="amber">Open</Badge>;
  }
}

function CommissionPeriods() {
  const { data, isLoading, error, refetch } = useQuery(() => api.admin.commissionPeriods.list());
  const [confirm, setConfirm] = useState<{ month: string; value: number; action: PeriodAction } | null>(null);
  const [working, setWorking] = useState(false);
  const cur = currentMonth();

  async function doAction(month: string, action: PeriodAction) {
    setWorking(true);
    try {
      if (action === 'lock') await api.admin.commissionPeriods.lock(month);
      else if (action === 'approve') await api.admin.commissionPeriods.approve(month);
      else await api.admin.commissionPeriods.markPaid(month);
      setConfirm(null);
      refetch();
    } catch (e) {
      alert(`Action failed: ${(e as Error).message}`);
    } finally {
      setWorking(false);
    }
  }

  function actionCell(p: CommissionPeriodRow) {
    if (p.status === 'paid') return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Completed</span>;
    if (p.status === 'open' && p.period_month >= cur) {
      return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Cannot lock current / future period</span>;
    }
    const action: PeriodAction = p.status === 'open' ? 'lock' : p.status === 'locked' ? 'approve' : 'mark-paid';
    return (
      <ActionButton
        onClick={() => setConfirm({ month: p.period_month, value: p.total_commission, action })}
        variant={COMMISSION_ACTION_LABELS[action].variant}
      >
        {COMMISSION_ACTION_LABELS[action].button}
      </ActionButton>
    );
  }

  return (
    <>
      <div style={{ background: 'var(--color-amber-bg)', border: '0.5px solid var(--color-amber)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12, color: 'var(--color-amber-text)', marginBottom: 10 }}>
        Commission periods are grouped by the month an invoice was <strong>billed</strong>, and progress
        through <strong>Open → Locked → Approved → Paid</strong>. Open periods are rebuilt on every data
        refresh; locking a period stops that, so lock only once the month's invoicing is final.
        Each transition is irreversible and recorded in the audit log.
      </div>

      <Card>
        <CardHeader><span style={{ fontSize: 12, fontWeight: 500 }}>All commission periods</span></CardHeader>
        {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : (data?.periods ?? []).length === 0 ? (
          <EmptyState message="No commission periods yet — run a refresh once a rate schedule is in place." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Period</Th>
                <Th>Invoiced services</Th>
                <Th>Commission</Th>
                <Th>Reps</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.periods ?? []).map((p) => (
                <Tr key={p.period_month}>
                  <Td bold>
                    {fmtMonth(p.period_month)}{' '}
                    {p.period_month >= cur && <Badge variant="blue">current</Badge>}
                  </Td>
                  <Td muted>{fmtCurrency(p.total_commissionable)}</Td>
                  <Td bold>{fmtCurrency(p.total_commission)}</Td>
                  <Td muted>{p.rep_count === 0 ? '—' : `${p.rep_count} rep${p.rep_count === 1 ? '' : 's'}`}</Td>
                  <Td>{commissionStateBadge(p.status)}</Td>
                  <Td>{actionCell(p)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {confirm && (() => {
        const meta = COMMISSION_ACTION_LABELS[confirm.action];
        return (
          <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 'var(--radius-lg)', padding: '48px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
            <div style={{ background: 'var(--color-surface)', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', width: 400 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{meta.title}: {fmtMonth(confirm.month)}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                {meta.description}<br /><br />
                Total commission: <strong>{fmtCurrency(confirm.value)}</strong>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <ActionButton onClick={() => setConfirm(null)} variant="default">Cancel</ActionButton>
                <ActionButton onClick={() => doAction(confirm.month, confirm.action)} variant={meta.variant}>
                  {working ? 'Processing…' : meta.button}
                </ActionButton>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminPeriodsPage() {
  const { user } = useAuth();
  const has = (key: string) => user?.permissions?.includes(key) === true;
  const canBonus = has('admin_periods');
  const canCommission = has('admin_commission_periods');
  const [tab, setTab] = useUrlState('ledger', canBonus ? 'bonus' : 'commission');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Period management</SectionLabel>
        {canBonus && canCommission && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <ActionButton variant={tab === 'bonus' ? 'primary' : 'default'} onClick={() => setTab('bonus')}>Bonuses</ActionButton>
            <ActionButton variant={tab === 'commission' ? 'primary' : 'default'} onClick={() => setTab('commission')}>Commissions</ActionButton>
          </span>
        )}
      </div>
      {tab === 'commission' && canCommission ? <CommissionPeriods />
        : canBonus ? <BonusPeriods />
        : <EmptyState message="You don't have access to either period ledger." />}
    </>
  );
}

function BonusPeriods() {
  const { data, isLoading, error, refetch } = useQuery(() => api.admin.periods.list());
  const [confirm, setConfirm] = useState<{ month: string; value: number; action: PeriodAction } | null>(null);
  const [working, setWorking] = useState(false);

  const cur = currentMonth();

  async function doAction(month: string, action: PeriodAction) {
    setWorking(true);
    try {
      if (action === 'lock')        await api.admin.periods.lock(month);
      else if (action === 'approve') await api.admin.periods.approve(month);
      else if (action === 'mark-paid') await api.admin.periods.markPaid(month);
      setConfirm(null);
      refetch();
    } catch (e) {
      alert(`Action failed: ${(e as Error).message}`);
    } finally {
      setWorking(false);
    }
  }

  function nextAction(p: PeriodRow): { action: PeriodAction; label: string } | null {
    const state = periodState(p);
    const isCurrent = p.attribution_month >= cur;
    if (state === 'open' && !isCurrent)  return { action: 'lock', label: ACTION_LABELS.lock.button };
    if (state === 'locked')              return { action: 'approve', label: ACTION_LABELS.approve.button };
    if (state === 'approved')            return { action: 'mark-paid', label: ACTION_LABELS['mark-paid'].button };
    return null;
  }

  function actionCell(p: PeriodRow) {
    const state = periodState(p);
    const isCurrent = p.attribution_month >= cur;
    const next = nextAction(p);
    if (state === 'paid') return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Completed</span>;
    if (state === 'no-snapshot') return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Run refresh to create snapshot</span>;
    if (state === 'open' && isCurrent) return <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Cannot lock current / future period</span>;
    if (!next) return null;
    return (
      <ActionButton
        onClick={() => setConfirm({ month: p.attribution_month, value: Number(p.total_shipped_value), action: next.action })}
        variant={ACTION_LABELS[next.action].variant}
      >
        {next.label}
      </ActionButton>
    );
  }

  return (
    <>
      <div style={{ background: 'var(--color-amber-bg)', border: '0.5px solid var(--color-amber)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 12, color: 'var(--color-amber-text)', marginBottom: 10 }}>
        Periods progress through <strong>Open → Locked → Approved → Paid</strong>. Each transition is irreversible and recorded in the audit log.
        Only lock a period once data is final; only approve once bonuses are reviewed; only mark paid once payment has been processed.
      </div>

      <Card>
        <CardHeader><span style={{ fontSize: 12, fontWeight: 500 }}>All attribution periods</span></CardHeader>

        {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : (
          <Table>
            <thead>
              <tr>
                <Th>Period</Th>
                <Th>Shipped value</Th>
                <Th>Records</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {(data?.periods ?? []).map((p) => {
                const isCurrent = p.attribution_month >= cur;
                const state = periodState(p);
                return (
                  <Tr key={p.attribution_month}>
                    <Td bold>
                      {fmtMonth(p.attribution_month)}{' '}
                      {isCurrent && <Badge variant="blue">current</Badge>}
                    </Td>
                    <Td bold>
                      {fmtCurrency(Number(p.total_shipped_value))}
                      {isCurrent && <span style={{ color: 'var(--color-text-muted)', fontSize: 11, marginLeft: 4 }}>(in progress)</span>}
                    </Td>
                    <Td muted>
                      {p.has_snapshot ? `${p.record_count} user${p.record_count === 1 ? '' : 's'}` : '—'}
                    </Td>
                    <Td>{stateBadge(state)}</Td>
                    <Td>{actionCell(p)}</Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {confirm && (() => {
        const meta = ACTION_LABELS[confirm.action];
        return (
          <div style={{ background: 'rgba(0,0,0,0.4)', borderRadius: 'var(--radius-lg)', padding: '48px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
            <div style={{ background: 'var(--color-surface)', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', width: 400 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{meta.title}: {fmtMonth(confirm.month)}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                {meta.description}<br /><br />
                Total shipped value: <strong>{fmtCurrency(confirm.value)}</strong>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <ActionButton onClick={() => setConfirm(null)} variant="default">Cancel</ActionButton>
                <ActionButton onClick={() => doAction(confirm.month, confirm.action)} variant={meta.variant}>
                  {working ? 'Processing…' : meta.button}
                </ActionButton>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}
